import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import Paper from '@base/Paper/Paper';
import P1 from '@base/P1/P1';
import P2 from '@base/P2/P2';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import { Fonts, mediaQuery, Spacings } from '@styles/index';
import NextImage from '@base/Image';
import Tooltip from '@base/Tooltip/Tooltip';
import { useState } from 'react';
import Me from '@models/Me';
import { getMe } from '@api/selectors/auth';
import useMyInfo from '@api/hooks/auth/useMyInfo';

const GoalCardContainer = styled(Paper)`
  grid-column: ${({ $isMyGoal }) => ($isMyGoal ? 'span 9' : 'span 6')};
  position: relative;
  padding: ${Spacings.SPACING_6B};
  background: var(--color-base-1);
  margin-top: ${Spacings.SPACING_4B};
  border-radius: 8px;
  display: ${({ $isMyGoal }) => ($isMyGoal ? 'flex' : 'block')};
  align-items: center;
  cursor: pointer;
  :hover {
    box-shadow: ${boxShadowGenerator({
      yOffset: Spacings.SPACING_1B,
      blur: Spacings.SPACING_2B
    })};
  }
  transition: box-shadow 0.2s ease-in-out;
  ${mediaQuery.uptoTablet} {
    grid-column: span 9;
    display: block;
  }
  ${mediaQuery.uptoMobile} {
    display: flex;
    align-items: center;
  }
`;

const GoalName = styled(P1)`
  color: ${({ $isAvailable }) =>
    $isAvailable ? 'var(--color-text-primary)' : 'var(--color-text-secondary)'};
  font-weight: ${Fonts.SEMIBOLD};
  ${mediaQuery.uptoMobile} {
    max-width: 170px;
  }
`;

const GoalIcon = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-right: ${({ $isMyGoal }) => ($isMyGoal ? Spacings.SPACING_5B : 0)};
  margin-bottom: ${({ $isMyGoal }) => ($isMyGoal ? 0 : Spacings.SPACING_5B)};
  ${mediaQuery.uptoMobile} {
    margin-bottom: 0;
    margin-right: ${Spacings.SPACING_4B};
  }
`;

const CoursesLength = styled(P2)`
  margin-top: ${Spacings.SPACING_1B};
  ${mediaQuery.uptoMobile} {
    margin-top: ${Spacings.SPACING_2};
  }
`;

const Tags = styled.div`
  padding: ${Spacings.SPACING_2} ${Spacings.SPACING_1B};
  border-radius: ${Spacings.SPACING_1B};
  background-color: var(--color-divider);
  color: var(--color-text-secondary);
  font-size: ${Fonts.EXTRA_SMALL};
  font-weight: ${Fonts.BOLD};
  ${mediaQuery.uptoMobile} {
    display: none;
  }
`;

const StyTooltip = styled(Tooltip)`
  .tooltip {
    width: 290px;
    text-align: center;
    font-size: ${Fonts.SMALL};
    font-weight: ${Fonts.SEMIBOLD};
  }
`;

const GoalIsComingSoonInfoCard = ({
  goal,
  isMyGoal,
  handleGoalCardClick,
  role,
  subscriptionInfo,
  isFollowedGoal
}) => {
  const [openTooltip, setOpenTooltip] = useState(false);

  const onMouseEnter = () => {
    if (goal.isForeignGoal && !goal.isAvailable) setOpenTooltip(true);
  };

  const onMouseLeave = () => {
    setOpenTooltip(false);
  };

  return (
    <StyTooltip
      open={openTooltip}
      title="This goal will be coming soon"
      padding={Spacings.SPACING_4B}
      borderRadius={Spacings.SPACING_2B}
    >
      <GoalCardContainer
        className="goal-card-container"
        $isMyGoal={isMyGoal}
        onClick={handleGoalCardClick}
        role={role}
        onMouseOver={onMouseEnter}
        onMouseOut={onMouseLeave}
      >
        {goal.icon && (
          <GoalIcon $isMyGoal={isMyGoal}>
            <NextImage src={goal.icon} alt={goal.name} height={40} width={40} />
            <Tags>SOON</Tags>
          </GoalIcon>
        )}
        <GoalName $isAvailable={false}>{goal.name}</GoalName>
        {!isMyGoal && (
          <CoursesLength color="var(--color-text-secondary)">
            {subscriptionInfo || (isFollowedGoal && 'Free plan')}
          </CoursesLength>
        )}
      </GoalCardContainer>
    </StyTooltip>
  );
};

const GoalCardInfoCard = ({
  goal,
  isMyGoal,
  handleGoalCardClick,
  role,
  subscriptionInfo,
  isFollowedGoal
}) => {
  return (
    <GoalCardContainer
      className="goal-card-container"
      $isMyGoal={isMyGoal}
      onClick={handleGoalCardClick}
      role={role}
    >
      {goal.icon && (
        <GoalIcon $isMyGoal={isMyGoal}>
          <NextImage src={goal.icon} alt={goal.name} height={40} width={40} />
        </GoalIcon>
      )}
      <GoalName $isAvailable>{goal.name}</GoalName>
      {!isMyGoal && (
        <CoursesLength color="var(--color-text-secondary)">
          {subscriptionInfo || (isFollowedGoal && 'Free plan')}
        </CoursesLength>
      )}
    </GoalCardContainer>
  );
};

const GoalCard = ({ goal, isMyGoal, role, onClick, followedGoals = [] }) => {
  const me = useMyInfo(getMe);
  if (!goal || !goal.name || !goal.uid) return null;
  const subscriptionInfo = Me.getSubscriptionInfo(me, goal.uid);
  const isFollowedGoal = !!followedGoals.find(
    (followedGoal) => followedGoal.uid === goal.uid
  );

  const handleGoalCardClick = () => {
    if (goal.isForeignGoal && !goal.isAvailable) return null;
    const isSubscribedGoal = Me.hasActiveSubscriptionToGoal(me, goal.uid);
    onClick({
      isMyGoal,
      isFollowedGoal,
      isSubscribedGoal,
      goal,
      evtSource: 'Default List'
    });
  };

  if (goal.isForeignGoal && !goal.isAvailable)
    return (
      <GoalIsComingSoonInfoCard
        handleGoalCardClick={handleGoalCardClick}
        role={role}
        isMyGoal={isMyGoal}
        subscriptionInfo={subscriptionInfo}
        isFollowedGoal={isFollowedGoal}
        goal={goal}
      />
    );

  return (
    <GoalCardInfoCard
      handleGoalCardClick={handleGoalCardClick}
      role={role}
      isMyGoal={isMyGoal}
      subscriptionInfo={subscriptionInfo}
      isFollowedGoal={isFollowedGoal}
      goal={goal}
    />
  );
};

export default GoalCard;

GoalCard.propTypes = {
  goal: PropTypes.shape({
    icon: PropTypes.string,
    name: PropTypes.string,
    uid: PropTypes.string
  }).isRequired,
  isMyGoal: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  role: PropTypes.string
};

GoalCard.defaultProps = {
  role: ''
};
